import { ColDef, ColumnMovedEvent } from 'ag-grid-community';
import { useSafeState } from 'helpers/hooks';
import { trackEventBudget } from 'helpers/trackerHelpers';
import { useCallback, useMemo } from 'react';

/**
 * Allows to sync the rowGroupIndex of the column definitions
 * whenever the user changes the columns order, thus effectively
 * changing the grouping order as well.
 * @param colDefs Available column definitions
 */
export const useFlexibleRowGroupings = (
    colDefs: ColDef[],
    onGroupChangedTrackEvent: string,
) => {
    const [groupFields, setGroupFields] = useSafeState<string[]>(
        colDefs
            .filter((c) => c.rowGroupIndex !== undefined)
            .map((c) => c.field ?? ''),
        (oldFields, newFields) =>
            oldFields === newFields ||
            (oldFields.length === newFields.length &&
                oldFields.every((f, i) => f === newFields[i])),
    );

    const updatedColDefs = useMemo(
        () =>
            colDefs.map((cd) => {
                if (cd.rowGroupIndex === undefined || cd.field === undefined) {
                    return cd;
                }
                const newGroupIndex = groupFields.indexOf(cd.field);
                if (newGroupIndex === -1) return cd;
                return {
                    ...cd,
                    rowGroupIndex: newGroupIndex,
                };
            }),
        [groupFields, JSON.stringify(colDefs)],
    );

    const onColumnMoved = useCallback(
        (event: ColumnMovedEvent) => {
            if (!event.finished) return;
            trackEventBudget(onGroupChangedTrackEvent);
            setGroupFields((oldFields) => {
                if (
                    event.toIndex === undefined ||
                    event.toIndex >= oldFields.length
                ) {
                    return oldFields;
                }
                const fieldToMove = event.column?.getColDef().showRowGroup;
                if (typeof fieldToMove !== 'string') {
                    return oldFields;
                }
                const movedFieldOldIndex = oldFields.indexOf(fieldToMove);
                if (movedFieldOldIndex === -1) return oldFields;
                const newFields = [...oldFields];
                newFields.splice(movedFieldOldIndex, 1);
                newFields.splice(event.toIndex, 0, fieldToMove);
                return newFields;
            });
        },
        [setGroupFields],
    );

    return { updatedColDefs, onColumnMoved };
};
